import React, { useEffect, useMemo, useState } from "react";
import { api, phpFmt } from "../lib/api";
import ProtectedRoute from "../components/ProtectedRoute";
import { toast, Toaster } from "sonner";
import { CheckCircle2, X, MapPin, Mail, Phone, User2, Clock, Image as ImageIcon, Plus, Trash2, Map as MapIcon, Download, Send, ShieldCheck, Pencil, Eye, EyeOff } from "lucide-react";
import { formatApiError } from "../lib/auth";
import { API } from "../lib/api";

// Relative-time helper for the "last activity" badge
function timeAgo(iso) {
    if (!iso) return "";
    const then = new Date(iso).getTime();
    if (Number.isNaN(then)) return "";
    const diffSec = Math.max(0, Math.floor((Date.now() - then) / 1000));
    if (diffSec < 60) return `${diffSec}s ago`;
    if (diffSec < 3600) return `${Math.floor(diffSec / 60)}m ago`;
    if (diffSec < 86400) return `${Math.floor(diffSec / 3600)}h ago`;
    return `${Math.floor(diffSec / 86400)}d ago`;
}

// --- Tiny modal shell used across this page ---
function Modal({ open, onClose, title, children, maxWidth = "max-w-md" }) {
    if (!open) return null;
    return (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4" role="dialog" aria-modal="true">
            <div className="absolute inset-0 bg-zinc-950/60" onClick={onClose} />
            <div className={`relative bg-white border border-zinc-200 w-full ${maxWidth} shadow-xl`}>
                <div className="flex items-center justify-between border-b border-zinc-200 p-4">
                    <div className="font-display text-lg font-semibold">{title}</div>
                    <button onClick={onClose} className="text-zinc-500 hover:text-zinc-900" aria-label="Close">
                        <X className="h-4 w-4" strokeWidth={1.5} />
                    </button>
                </div>
                <div className="p-5">{children}</div>
            </div>
        </div>
    );
}

function CitiesCard() {
    const [cities, setCities] = useState([]);
    const [regions, setRegions] = useState([]);
    const [newCity, setNewCity] = useState("");
    const [newRegion, setNewRegion] = useState("");
    const [busy, setBusy] = useState(false);
    const [editing, setEditing] = useState(null); // { name, region }
    const [editRegion, setEditRegion] = useState("");

    const load = async () => {
        try {
            const [{ data: c }, { data: r }] = await Promise.all([
                api.get("/cities"),
                api.get("/regions"),
            ]);
            setCities(c);
            setRegions(r);
            if (!newRegion && r.length) setNewRegion(r[0]);
        } catch { /* noop */ }
    };

    useEffect(() => { load(); /* eslint-disable-next-line */ }, []);

    const add = async () => {
        const name = newCity.trim();
        if (!name) return;
        setBusy(true);
        try {
            await api.post("/admin/cities", { name, region: newRegion || "Unassigned" });
            setNewCity("");
            toast.success(`Added ${name}`);
            load();
        } catch (e) {
            toast.error(formatApiError(e.response?.data?.detail) || "Could not add city");
        } finally {
            setBusy(false);
        }
    };

    const remove = async (name, count) => {
        if (count > 0) {
            toast.error(`Cannot remove ${name} — ${count} center(s) still listed here.`);
            return;
        }
        if (!window.confirm(`Remove ${name}?`)) return;
        try {
            await api.delete(`/admin/cities/${encodeURIComponent(name)}`);
            toast.success(`Removed ${name}`);
            load();
        } catch (e) {
            toast.error(formatApiError(e.response?.data?.detail) || "Could not remove");
        }
    };

    const openReassign = (city) => {
        setEditing(city);
        setEditRegion(city.region || "Unassigned");
    };

    const saveReassign = async () => {
        if (!editing) return;
        if (editRegion === editing.region) {
            setEditing(null);
            return;
        }
        try {
            await api.put(`/admin/cities/${encodeURIComponent(editing.name)}`, { region: editRegion });
            toast.success(`${editing.name} → ${editRegion}`);
            setEditing(null);
            load();
        } catch (e) {
            toast.error(formatApiError(e.response?.data?.detail) || "Could not update");
        }
    };

    // Group cities by region (stable order: follow `regions` list)
    const grouped = useMemo(() => {
        const byRegion = new Map();
        for (const c of cities) {
            const key = c.region || "Unassigned";
            if (!byRegion.has(key)) byRegion.set(key, []);
            byRegion.get(key).push(c);
        }
        const ordered = [];
        for (const r of regions) {
            if (byRegion.has(r)) ordered.push([r, byRegion.get(r)]);
        }
        // Any region not in the static list still shows up at the end
        for (const [r, list] of byRegion) {
            if (!regions.includes(r)) ordered.push([r, list]);
        }
        return ordered;
    }, [cities, regions]);

    return (
        <div data-testid="cities-card" className="border border-zinc-200 bg-white">
            <div className="p-5 border-b border-zinc-200 flex items-center justify-between">
                <div className="flex items-center gap-2">
                    <MapIcon className="h-4 w-4" strokeWidth={1.5} />
                    <div className="font-display text-lg font-semibold">Cities by Region</div>
                </div>
                <div className="text-[10px] uppercase tracking-label text-zinc-500">{cities.length} listed</div>
            </div>
            <div className="p-5">
                <div className="flex flex-col sm:flex-row sm:items-stretch gap-2">
                    <input
                        data-testid="new-city-input"
                        value={newCity}
                        onChange={(e) => setNewCity(e.target.value)}
                        onKeyDown={(e) => e.key === "Enter" && add()}
                        placeholder="Add new city (e.g. Iloilo City)"
                        className="flex-1 border border-zinc-300 px-3 py-2.5 text-sm focus:border-zinc-950 focus:outline-none"
                    />
                    <select
                        data-testid="new-city-region"
                        value={newRegion}
                        onChange={(e) => setNewRegion(e.target.value)}
                        className="border border-zinc-300 px-3 py-2.5 text-sm focus:border-zinc-950 focus:outline-none sm:w-60"
                    >
                        {regions.map((r) => (<option key={r} value={r}>{r}</option>))}
                    </select>
                    <button
                        data-testid="add-city-btn"
                        onClick={add}
                        disabled={busy || !newCity.trim()}
                        className="inline-flex items-center justify-center gap-1.5 bg-zinc-950 text-white px-4 py-2.5 text-sm hover:bg-[#FF6A13] transition-colors disabled:opacity-40"
                    >
                        <Plus className="h-4 w-4" strokeWidth={1.5} /> Add
                    </button>
                </div>

                <div className="mt-6 space-y-6">
                    {grouped.map(([region, list]) => (
                        <div key={region} data-testid={`region-group-${region}`}>
                            <div className="flex items-center justify-between mb-2">
                                <div className="text-[10px] uppercase tracking-label text-zinc-500 font-semibold">{region}</div>
                                <div className="text-[10px] uppercase tracking-label text-zinc-400">{list.length} {list.length === 1 ? "city" : "cities"}</div>
                            </div>
                            <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-2">
                                {list.map((c) => (
                                    <div key={c.name} data-testid={`city-row-${c.name}`} className="flex items-center justify-between border border-zinc-200 px-3 py-2">
                                        <div>
                                            <div className="text-sm font-medium">{c.name}</div>
                                            <div className="text-[10px] uppercase tracking-label text-zinc-500">
                                                {c.count} {c.count === 1 ? "center" : "centers"}
                                            </div>
                                        </div>
                                        <div className="flex items-center gap-1.5">
                                            <button
                                                data-testid={`edit-city-${c.name}`}
                                                onClick={() => openReassign(c)}
                                                className="text-zinc-500 hover:text-zinc-900"
                                                aria-label={`Reassign region for ${c.name}`}
                                                title="Change region"
                                            >
                                                <Pencil className="h-3.5 w-3.5" strokeWidth={1.5} />
                                            </button>
                                            <button
                                                data-testid={`remove-city-${c.name}`}
                                                onClick={() => remove(c.name, c.count)}
                                                className="text-zinc-500 hover:text-red-600 disabled:opacity-30"
                                                aria-label={`Remove ${c.name}`}
                                                disabled={c.count > 0}
                                                title={c.count > 0 ? "Cannot remove — centers are listed in this city" : `Remove ${c.name}`}
                                            >
                                                <Trash2 className="h-4 w-4" strokeWidth={1.5} />
                                            </button>
                                        </div>
                                    </div>
                                ))}
                            </div>
                        </div>
                    ))}
                </div>
            </div>

            <Modal open={!!editing} onClose={() => setEditing(null)} title={`Reassign ${editing?.name || ""}`}>
                <div className="text-xs uppercase tracking-label text-zinc-500 mb-2">Region</div>
                <select
                    data-testid="reassign-region-select"
                    value={editRegion}
                    onChange={(e) => setEditRegion(e.target.value)}
                    className="w-full border border-zinc-300 px-3 py-2.5 text-sm focus:border-zinc-950 focus:outline-none"
                >
                    {regions.map((r) => (<option key={r} value={r}>{r}</option>))}
                </select>
                <div className="mt-5 flex items-center justify-end gap-2">
                    <button onClick={() => setEditing(null)} className="border border-zinc-300 px-4 py-2 text-sm hover:border-zinc-950">Cancel</button>
                    <button
                        data-testid="reassign-region-save"
                        onClick={saveReassign}
                        className="bg-zinc-950 text-white px-4 py-2 text-sm hover:bg-[#FF6A13] transition-colors"
                    >
                        Save
                    </button>
                </div>
            </Modal>
        </div>
    );
}

function Body() {
    const [tab, setTab] = useState("pending");
    const [centers, setCenters] = useState([]);
    const [stats, setStats] = useState(null);
    const [loading, setLoading] = useState(true);
    const [approving, setApproving] = useState(null); // center to approve
    const [approveShowContact, setApproveShowContact] = useState(false);

    const load = async () => {
        setLoading(true);
        const [c, s] = await Promise.all([
            api.get(`/admin/centers?status=${tab}`),
            api.get("/admin/stats"),
        ]);
        setCenters(c.data);
        setStats(s.data);
        setLoading(false);
    };

    useEffect(() => { load(); /* eslint-disable-next-line */ }, [tab]);

    const openApprove = (center) => {
        setApproving(center);
        setApproveShowContact(false); // safe default: hidden
    };

    const confirmApprove = async () => {
        if (!approving) return;
        try {
            await api.post(`/admin/centers/${approving.center_id}/approve`, { show_public_contact: approveShowContact });
            toast.success("Approved");
            setApproving(null);
            load();
        } catch { toast.error("Failed to approve"); }
    };

    const reject = async (id) => {
        try { await api.post(`/admin/centers/${id}/reject`); toast.success("Rejected"); load(); }
        catch { toast.error("Failed to reject"); }
    };
    const remove = async (id, name) => {
        if (!window.confirm(`Permanently delete "${name}"? This cannot be undone.`)) return;
        try { await api.delete(`/admin/centers/${id}`); toast.success("Center deleted"); load(); }
        catch { toast.error("Failed to delete"); }
    };
    const toggleVisibility = async (c) => {
        const next = !c.show_public_contact;
        try {
            await api.post(`/admin/centers/${c.center_id}/visibility`, { show_public_contact: next });
            toast.success(next ? "Contact info now visible on listing" : "Contact info hidden — inquiries route via FWG");
            load();
        } catch { toast.error("Could not update visibility"); }
    };

    const exportCsv = (what) => {
        // Cookie-auth works on same-origin fetch via window.open
        const url = `${API}/admin/export/${what}.csv`;
        window.open(url, "_blank", "noopener,noreferrer");
    };

    const sendWeeklyDigest = async () => {
        try {
            const { data } = await api.post("/admin/send-weekly-summary");
            if (data.sent) toast.success("Weekly summary sent to FWG inbox");
            else toast.error("Could not send — check Resend API key");
        } catch {
            toast.error("Send failed");
        }
    };

    return (
        <div className="pt-24 min-h-screen">
            <Toaster richColors position="top-center" />
            <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8 py-12">
                <div className="text-[10px] uppercase tracking-label text-zinc-500">Admin · FWG</div>
                <h1 className="font-display text-5xl font-semibold tracking-tighter mt-2">Control Room</h1>

                {stats && (
                    <div className="mt-8 grid grid-cols-2 md:grid-cols-6 gap-px bg-zinc-200 border border-zinc-200">
                        {[
                            ["Pending", stats.pending],
                            ["Approved", stats.approved],
                            ["Rejected", stats.rejected],
                            ["Active Agreements", stats.active_agreements],
                            ["Providers", stats.providers],
                            ["Seekers", stats.clients],
                        ].map(([l, v]) => (
                            <div key={l} className="bg-white p-5">
                                <div className="text-[10px] uppercase tracking-label text-zinc-500">{l}</div>
                                <div className="font-display text-4xl font-semibold mt-1">{v}</div>
                            </div>
                        ))}
                    </div>
                )}

                <div className="mt-10 flex items-center gap-2">
                    {["pending", "approved", "rejected"].map((t) => (
                        <button key={t} data-testid={`admin-tab-${t}`} onClick={() => setTab(t)}
                            className={`px-4 py-2 text-xs uppercase tracking-label border ${tab === t ? "bg-zinc-950 text-white border-zinc-950" : "bg-white text-zinc-700 border-zinc-300 hover:border-zinc-950"}`}>
                            {t}
                        </button>
                    ))}
                </div>

                <div className="mt-10">
                    <CitiesCard />
                </div>

                {/* CSV export bar + weekly digest trigger */}
                <div className="mt-10 border border-zinc-200 bg-white p-4 flex flex-wrap items-center gap-3">
                    <div className="flex items-center gap-2">
                        <Download className="h-4 w-4 text-zinc-600" strokeWidth={1.5} />
                        <div className="text-[10px] uppercase tracking-label text-zinc-500">Export data</div>
                    </div>
                    {[
                        ["centers", "Centers"],
                        ["providers", "Providers"],
                        ["clients", "Seekers"],
                        ["agreements", "Agreements"],
                    ].map(([k, label]) => (
                        <button key={k} data-testid={`export-${k}-btn`} onClick={() => exportCsv(k)}
                            className="inline-flex items-center gap-1.5 border border-zinc-300 px-3 py-1.5 text-xs uppercase tracking-label hover:border-zinc-950 transition-colors">
                            <Download className="h-3.5 w-3.5" strokeWidth={1.5} /> {label}.csv
                        </button>
                    ))}
                    <div className="flex-1" />
                    <button data-testid="send-weekly-digest-btn" onClick={sendWeeklyDigest}
                        title="Preview the weekly email now — auto-sends every Monday 9am PHT"
                        className="inline-flex items-center gap-1.5 bg-zinc-950 text-white px-3 py-1.5 text-xs uppercase tracking-label hover:bg-[#FF6A13] transition-colors">
                        <Send className="h-3.5 w-3.5" strokeWidth={1.5} /> Send weekly summary now
                    </button>
                </div>

                <div className="mt-8">
                    {loading ? (
                        <div className="text-zinc-500 text-sm tracking-label uppercase">Loading…</div>
                    ) : centers.length === 0 ? (
                        <div data-testid="admin-empty" className="border border-zinc-200 bg-zinc-50 p-12 text-center">
                            <div className="font-display text-2xl font-semibold">No {tab} centers.</div>
                        </div>
                    ) : (
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                            {centers.map((c) => (
                                <div key={c.center_id} data-testid={`admin-center-${c.center_id}`} className="border border-zinc-200 bg-white">
                                    <div className="grid grid-cols-3 gap-px bg-zinc-100">
                                        <div className="col-span-2 aspect-[4/3] overflow-hidden bg-zinc-100">
                                            {c.photos?.[0] ? (
                                                <img src={c.photos[0]} alt="" className="h-full w-full object-cover" />
                                            ) : (
                                                <div className="h-full w-full grid place-items-center text-zinc-400"><ImageIcon className="h-6 w-6" strokeWidth={1.5} /></div>
                                            )}
                                        </div>
                                        <div className="flex flex-col">
                                            {c.photos?.slice(1, 3).map((p, i) => (
                                                <div key={i} className="flex-1 overflow-hidden bg-zinc-100">
                                                    <img src={p} alt="" className="h-full w-full object-cover" />
                                                </div>
                                            ))}
                                        </div>
                                    </div>
                                    <div className="p-5">
                                        <div className="flex items-start justify-between gap-3">
                                            <div>
                                                <div className="text-[10px] uppercase tracking-label text-zinc-500 flex items-center gap-1.5">
                                                    <MapPin className="h-3 w-3" strokeWidth={1.5} /> {c.city} · {c.public_area}
                                                </div>
                                                <div className="font-display text-2xl font-semibold tracking-tight mt-1">{c.name}</div>
                                                <div className="font-display text-lg font-semibold mt-1">{phpFmt(c.monthly_rate_php)} <span className="text-[10px] text-zinc-500 uppercase tracking-label">/ mo</span></div>
                                            </div>
                                            <span className="text-[10px] uppercase tracking-label bg-zinc-100 px-2 py-1 flex items-center gap-1">
                                                <Clock className="h-3 w-3" strokeWidth={1.5} /> {c.status}
                                            </span>
                                        </div>
                                        <p className="mt-3 text-sm text-zinc-600 line-clamp-2">{c.description}</p>

                                        {c.review_action && c.reviewed_by_email && (
                                            <div data-testid={`review-activity-${c.center_id}`}
                                                className="mt-3 flex items-center gap-2 text-[11px] text-zinc-600 bg-zinc-50 border border-zinc-200 px-2.5 py-1.5">
                                                <ShieldCheck className="h-3.5 w-3.5 text-zinc-500" strokeWidth={1.5} />
                                                <span className="uppercase tracking-label text-[10px] text-zinc-500">{c.review_action}</span>
                                                <span className="text-zinc-400">·</span>
                                                <span className="font-medium">{c.reviewed_by_email}</span>
                                                <span className="text-zinc-400">·</span>
                                                <span>{timeAgo(c.reviewed_at)}</span>
                                            </div>
                                        )}

                                        <div className="mt-4 border border-zinc-200 p-3 bg-zinc-50 text-xs space-y-1.5">
                                            <div className="text-[10px] uppercase tracking-label text-zinc-500 mb-1">Private (FWG only)</div>
                                            <div className="flex items-center gap-2"><User2 className="h-3 w-3" strokeWidth={1.5} /> {c.contact_name_private}</div>
                                            <div className="flex items-center gap-2"><Mail className="h-3 w-3" strokeWidth={1.5} /> {c.contact_email_private}</div>
                                            <div className="flex items-center gap-2"><Phone className="h-3 w-3" strokeWidth={1.5} /> {c.contact_phone_private}</div>
                                            <div className="flex items-center gap-2"><MapPin className="h-3 w-3" strokeWidth={1.5} /> {c.street_address_private}</div>
                                        </div>

                                        {c.status === "pending" && (
                                            <div className="mt-5 flex items-center gap-3 flex-wrap">
                                                <button data-testid={`approve-${c.center_id}`} onClick={() => openApprove(c)}
                                                    className="inline-flex items-center gap-1.5 bg-zinc-950 text-white px-4 py-2 text-sm hover:bg-green-700 transition-colors">
                                                    <CheckCircle2 className="h-4 w-4" strokeWidth={1.5} /> Approve
                                                </button>
                                                <button data-testid={`reject-${c.center_id}`} onClick={() => reject(c.center_id)}
                                                    className="inline-flex items-center gap-1.5 bg-white border border-zinc-300 px-4 py-2 text-sm hover:border-zinc-950 transition-colors">
                                                    <X className="h-4 w-4" strokeWidth={1.5} /> Reject
                                                </button>
                                                <button data-testid={`delete-${c.center_id}`} onClick={() => remove(c.center_id, c.name)}
                                                    className="inline-flex items-center gap-1.5 text-red-700 hover:text-red-900 text-sm">
                                                    <Trash2 className="h-4 w-4" strokeWidth={1.5} /> Delete
                                                </button>
                                            </div>
                                        )}
                                        {c.status === "approved" && (
                                            <div className="mt-5 flex items-center gap-3 flex-wrap">
                                                <button
                                                    data-testid={`visibility-toggle-${c.center_id}`}
                                                    onClick={() => toggleVisibility(c)}
                                                    title={c.show_public_contact
                                                        ? "Direct contact is visible on public listing — click to hide and route inquiries via FWG"
                                                        : "Inquiries route via FWG — click to reveal direct contact on public listing (FWG-owned centers)"}
                                                    className={`inline-flex items-center gap-1.5 px-3 py-1.5 text-xs uppercase tracking-label border transition-colors ${c.show_public_contact
                                                        ? "bg-[#FF6A13] text-white border-[#FF6A13] hover:bg-[#e05a0d]"
                                                        : "bg-white text-zinc-700 border-zinc-300 hover:border-zinc-950"}`}
                                                >
                                                    {c.show_public_contact
                                                        ? (<><Eye className="h-3.5 w-3.5" strokeWidth={1.5} /> Contact public</>)
                                                        : (<><EyeOff className="h-3.5 w-3.5" strokeWidth={1.5} /> Inquire via FWG</>)}
                                                </button>
                                                <button data-testid={`delete-${c.center_id}`} onClick={() => remove(c.center_id, c.name)}
                                                    className="inline-flex items-center gap-1.5 bg-white border border-red-300 text-red-700 px-4 py-2 text-sm hover:bg-red-50 hover:border-red-500 transition-colors">
                                                    <Trash2 className="h-4 w-4" strokeWidth={1.5} /> Delete permanently
                                                </button>
                                            </div>
                                        )}
                                        {c.status === "rejected" && (
                                            <div className="mt-5 flex items-center gap-3 flex-wrap">
                                                <button data-testid={`delete-${c.center_id}`} onClick={() => remove(c.center_id, c.name)}
                                                    className="inline-flex items-center gap-1.5 bg-white border border-red-300 text-red-700 px-4 py-2 text-sm hover:bg-red-50 hover:border-red-500 transition-colors">
                                                    <Trash2 className="h-4 w-4" strokeWidth={1.5} /> Delete permanently
                                                </button>
                                            </div>
                                        )}
                                    </div>
                                </div>
                            ))}
                        </div>
                    )}
                </div>
            </div>

            <Modal open={!!approving} onClose={() => setApproving(null)} title={`Approve ${approving?.name || ""}`}>
                <p className="text-sm text-zinc-700 leading-relaxed">
                    This listing will go live on the marketplace immediately after approval.
                </p>
                <label
                    data-testid="approve-show-contact-label"
                    className="mt-4 flex items-start gap-3 border border-zinc-200 p-3 cursor-pointer hover:border-zinc-950"
                >
                    <input
                        data-testid="approve-show-contact-checkbox"
                        type="checkbox"
                        className="mt-0.5"
                        checked={approveShowContact}
                        onChange={(e) => setApproveShowContact(e.target.checked)}
                    />
                    <div>
                        <div className="text-sm font-semibold">Show direct contact on public listing</div>
                        <div className="mt-1 text-xs text-zinc-600 leading-relaxed">
                            Only turn ON for FWG-owned centers (Loud Crowd, Prime, etc.). For third-party
                            partner centers, leave OFF so all seeker inquiries route through FWG.
                        </div>
                    </div>
                </label>
                <div className="mt-5 flex items-center justify-end gap-2">
                    <button
                        onClick={() => setApproving(null)}
                        className="border border-zinc-300 px-4 py-2 text-sm hover:border-zinc-950"
                    >
                        Cancel
                    </button>
                    <button
                        data-testid="approve-confirm-btn"
                        onClick={confirmApprove}
                        className="bg-zinc-950 text-white px-4 py-2 text-sm hover:bg-green-700 transition-colors inline-flex items-center gap-1.5"
                    >
                        <CheckCircle2 className="h-4 w-4" strokeWidth={1.5} /> Approve & publish
                    </button>
                </div>
            </Modal>
        </div>
    );
}

export default function AdminDashboard() {
    return (
        <ProtectedRoute roles={["admin"]}>
            <Body />
        </ProtectedRoute>
    );
}
